import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { 
  AlertCircle, 
  ArrowLeft, 
  Share2, 
  CheckCircle, 
  ShieldCheck, 
  MapPin, 
  Calendar 
} from 'lucide-react';

import { ProfessionalProfileService } from '@/core/application/directory/ProfessionalProfileService';
import { PublicDoctorProfile } from '@/core/domain/types/profile.types';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import { useProfileStore } from '@/core/application/state/useProfileStore';
import { SmartAccordion } from '@/modules/consumer/components/SmartAccordion';
import { WebReadOnlyMap } from '@/presentation/components/UI/WebReadOnlyMap';

export function DoctorProfileScreen() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const initialData = location.state?.initialData;
  const doctorId = id || initialData?.id;

  const [profile, setProfile] = useState<PublicDoctorProfile | null>(() => {
    if (initialData) {
      return {
        perfil_usuario_id: doctorId,
        nombres_completos: initialData.nombre,
        apellidos_completos: '',
        avatar_url: initialData.imagen_url,
        titulo_principal: initialData.titulo_principal || 'Médico',
        especialidades: [initialData.especialidad].filter(Boolean),
        verificado_global: initialData.verificado,
        licencia_mpps: initialData.licencia_profesional || null,
        licencia_fmv: null,
        licencia_fpv: null,
        licencia_cmv: null,
        resumen_profesional: '',
        ciudad_nombre: '',
        estado_nombre: '',
        direccion_consultorio: '',
        latitud: null,
        longitud: null,
      } as any;
    }
    return null;
  });

  const [loading, setLoading] = useState(!initialData);
  const [loadingDetails, setLoadingDetails] = useState(true);

  useEffect(() => {
    const checkAuthAndLoad = async () => {
      if (!doctorId) return;
      
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const store = useProfileStore.getState();
        if (!store.hasChecked) {
          await store.fetchProfile();
        }
        if (!useProfileStore.getState().isProfileComplete) {
          navigate('/auth/onboarding', { replace: true });
          return;
        }
      }
      loadProfile();
    };
    checkAuthAndLoad();
  }, [doctorId]);

  const loadProfile = async () => {
    if (!doctorId) return;
    try {
      const data = await ProfessionalProfileService.getPublicProfileById(doctorId);
      setProfile(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setLoadingDetails(false);
    }
  };

  const getInitials = (first?: string, last?: string) => {
    const f = first ? first.charAt(0) : '';
    const l = last ? last.charAt(0) : '';
    return (f + l).toUpperCase() || '?';
  };

  const handleBookAppointment = () => {
    if (!profile) return;
    navigate('/app/Booking', {
      state: {
        profesionalUuid: doctorId,
        profesionalNombre: `${profile.nombres_completos} ${profile.apellidos_completos}`.trim(),
        profesionalTitulo: profile.titulo_principal || 'Profesional de salud',
      }
    });
  };

  const handleShare = async () => {
    if (!profile) return;
    const nombre = `${profile.nombres_completos} ${profile.apellidos_completos}`.trim();
    const url = globalThis.location.href;
    const mensaje = `${nombre} — ${profile.titulo_principal}\nConsulta su perfil en Aura:\n${url}`;
    
    if (navigator.share) {
      try { 
        await navigator.share({ title: nombre, text: mensaje, url }); 
      } catch { /* cancelado */ }
    } else {
      await navigator.clipboard.writeText(url);
      globalThis.alert(`¡Enlace copiado al portapapeles!\n${url}`);
    }
  };

  if (loading) {
    return (
      <div style={styles.centerContainer}>
        <div style={styles.spinner} />
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={styles.centerContainer}>
        <AlertCircle size={48} color="#94A3B8" />
        <p style={styles.errorText}>No pudimos encontrar este perfil.</p>
        <button style={styles.backBtn} onClick={() => navigate('/app/search')}>
          Volver al Directorio
        </button>
      </div>
    );
  }

  let licenciaPrincipal = null;
  if (profile.licencia_mpps) {
    if (/[GO]/i.test(profile.licencia_mpps)) {
      licenciaPrincipal = `G.O.: ${profile.licencia_mpps.replace(/\D/g, '')}`;
    } else {
      licenciaPrincipal = `MPPS: ${profile.licencia_mpps}`;
    }
  }

  const licencias = [
    licenciaPrincipal,
    profile.licencia_fmv ? `FMV: ${profile.licencia_fmv}` : null,
    profile.licencia_fpv ? `FPV: ${profile.licencia_fpv}` : null,
    profile.licencia_cmv ? `CMV: ${profile.licencia_cmv}` : null,
  ].filter(Boolean).join(' • ');

  const renderMap = () => {
    if (!profile?.latitud || !profile?.longitud) return null;
    return <WebReadOnlyMap latitude={profile.latitud} longitude={profile.longitud} theme={{ background: '#E2E8F0' }} />;
  };

  return (
    <div style={styles.container}>
      {/* HEADER NAVEGACIÓN */}
      <div style={styles.navHeader}>
        <button style={styles.iconBtn} onClick={() => globalThis.history.length > 2 ? navigate(-1) : navigate('/app/search')}>
          <ArrowLeft size={24} color="#0F172A" />
        </button>
        <button style={styles.iconBtn} onClick={handleShare}>
          <Share2 size={24} color="#0F172A" />
        </button>
      </div>

      <div style={styles.scrollContent}>
        {/* IDENTIDAD PRINCIPAL */}
        <div style={styles.identitySection}>
          {profile.avatar_url ? (
            <img src={profile.avatar_url} alt="Avatar" style={styles.avatarBig} />
          ) : (
            <div style={styles.avatarBigPlaceholder}>
              <span style={styles.avatarBigText}>
                {getInitials(profile.nombres_completos, profile.apellidos_completos)}
              </span>
            </div>
          )}

          <div style={styles.nameRow}>
            <h1 style={styles.fullName}>
              {profile.nombres_completos} {profile.apellidos_completos}
            </h1>
            {profile.verificado_global && (
              <CheckCircle size={22} color="#3B82F6" style={{ marginLeft: '6px' }} />
            )}
          </div>

          <h2 style={styles.professionTitle}>{profile.titulo_principal}</h2>
          
          {profile.especialidades.length > 0 && (
            <p style={styles.specialtiesText}>
              {profile.especialidades.join(', ')}
            </p>
          )}

          {licencias && (
            <div style={styles.licensePill}>
              <ShieldCheck size={14} color="#64748B" />
              <span style={styles.licenseText}>{licencias}</span>
            </div>
          )}
        </div>

        {/* SOBRE MÍ */}
        {(() => {
          if (loadingDetails) {
            return (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Sobre mí</h3>
                <div style={{ ...styles.aboutCard, display: 'flex', justifyContent: 'center', padding: '24px' }}>
                  <div style={styles.spinnerSmall} />
                </div>
              </div>
            );
          }
          if (profile.resumen_profesional) {
            return (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Sobre mí</h3>
                <div style={styles.aboutCard}>
                  <p style={styles.aboutText}>{profile.resumen_profesional}</p>
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* INFORMACION DEL PROFESIONAL (Smart Accordion) */}
        <div style={styles.section}>
          <SmartAccordion profesionalId={doctorId} onBookAppointment={handleBookAppointment} />
        </div>

        {/* UBICACIÓN */}
        {(() => {
          if (loadingDetails) {
            return (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Ubicación Principal</h3>
                <div style={{ ...styles.locationBox, display: 'flex', justifyContent: 'center', padding: '24px' }}>
                  <div style={styles.spinnerSmall} />
                </div>
              </div>
            );
          }
          if (profile.direccion_consultorio || profile.ciudad_nombre) {
            return (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Ubicación Principal</h3>
                <div style={styles.locationBox}>
                  <div style={styles.locationIconBox}>
                    <MapPin size={20} color="#3B82F6" />
                  </div>
                  <div style={styles.locationTextContent}>
                    <p style={styles.locationCity}>
                      {profile.ciudad_nombre}{profile.estado_nombre ? `, ${profile.estado_nombre}` : ''}
                    </p>
                    {Boolean(profile.direccion_consultorio) && (
                      <p style={styles.locationAddress}>{profile.direccion_consultorio}</p>
                    )}
                  </div>
                </div>
                {renderMap()}
              </div>
            );
          }
          return null;
        })()}
        
        {/* Espacio extra */}
        <div style={{ height: '100px' }} />
      </div>

      {/* FOOTER CTA */}
      <div style={styles.footerCTA}>
        <button 
          style={styles.ctaButton} 
          onClick={handleBookAppointment}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        >
          <Calendar size={20} color="#FFFFFF" style={{ marginRight: '8px' }} />
          Ver disponibilidad y agendar
        </button>
      </div>

      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    position: 'relative',
  },
  centerContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    padding: '24px',
    minHeight: '100vh',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #0F172A',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  spinnerSmall: {
    width: '24px',
    height: '24px',
    border: '3px solid #94A3B8',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  navHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    position: 'sticky',
    top: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    zIndex: 10,
  },
  iconBtn: {
    width: '40px',
    height: '40px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '20px',
    backgroundColor: '#F8FAFC',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  scrollContent: {
    padding: '10px 20px 0 20px',
    flex: 1,
    overflowY: 'auto',
  },
  identitySection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '32px',
  },
  avatarBig: {
    width: '110px',
    height: '110px',
    borderRadius: '55px',
    marginBottom: '16px',
    backgroundColor: '#F1F5F9',
    objectFit: 'cover',
  },
  avatarBigPlaceholder: {
    width: '110px',
    height: '110px',
    borderRadius: '55px',
    backgroundColor: '#0F172A',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '16px',
  },
  avatarBigText: {
    color: '#FFFFFF',
    fontSize: '40px',
    fontWeight: 700,
    letterSpacing: '2px',
  },
  nameRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: '6px',
  },
  fullName: {
    fontSize: '24px',
    fontWeight: 800,
    color: '#0F172A',
    textAlign: 'center',
    margin: 0,
  },
  professionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#334155',
    marginBottom: '4px',
    margin: 0,
  },
  specialtiesText: {
    fontSize: '14px',
    color: '#64748B',
    marginBottom: '12px',
    textAlign: 'center',
    margin: '4px 0 12px 0',
  },
  licensePill: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
    padding: '6px 12px',
    borderRadius: '16px',
    border: '1px solid #E2E8F0',
  },
  licenseText: {
    marginLeft: '6px',
    fontSize: '12px',
    color: '#64748B',
    fontWeight: 500,
  },
  section: {
    marginBottom: '28px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#0F172A',
    marginBottom: '16px',
    margin: '0 0 16px 0',
  },
  aboutCard: {
    padding: '16px',
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    border: '1px solid #E2E8F0',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  },
  aboutText: {
    fontSize: '16px',
    lineHeight: '26px',
    color: '#334155',
    margin: 0,
    whiteSpace: 'pre-wrap',
  },
  locationBox: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: '#F8FAFC',
    borderRadius: '16px',
    padding: '16px',
    border: '1px solid #E2E8F0',
    alignItems: 'center',
  },
  locationIconBox: {
    width: '40px',
    height: '40px',
    borderRadius: '20px',
    backgroundColor: '#DBEAFE',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: '16px',
    flexShrink: 0,
  },
  locationTextContent: {
    flex: 1,
  },
  locationCity: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#0F172A',
    marginBottom: '4px',
    margin: '0 0 4px 0',
  },
  locationAddress: {
    fontSize: '14px',
    color: '#64748B',
    lineHeight: '20px',
    margin: 0,
  },
  footerCTA: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#FFFFFF',
    padding: '16px 20px 32px 20px',
    borderTop: '1px solid #F1F5F9',
    boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
    zIndex: 9999,
  },
  ctaButton: {
    backgroundColor: '#0F172A',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '16px 24px',
    borderRadius: '100px',
    border: 'none',
    width: '100%',
    cursor: 'pointer',
    color: '#FFFFFF',
    fontSize: '16px',
    fontWeight: 700,
    transition: 'opacity 0.2s',
  },
  errorText: {
    fontSize: '16px',
    color: '#64748B',
    marginTop: '16px',
    marginBottom: '24px',
  },
  backBtn: {
    padding: '12px 24px',
    backgroundColor: '#F1F5F9',
    borderRadius: '8px',
    border: 'none',
    color: '#0F172A',
    fontWeight: 600,
    cursor: 'pointer',
  }
};
